import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Folder } from "@/types";
import { post, put } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

interface CreateFolderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onFolderCreated: () => void;
  folders?: Folder[];
  parentId?: number | null;
  renameFolder?: Folder | null;
}

export function CreateFolderDialog({
  isOpen,
  onClose,
  onFolderCreated,
  folders = [],
  parentId = null,
  renameFolder = null,
}: CreateFolderDialogProps) {
  const isRename = Boolean(renameFolder);
  const [folderName, setFolderName] = useState("");
  const [selectedParent, setSelectedParent] = useState("none");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!isOpen) return;
    setFolderName(renameFolder?.name || "");
    setSelectedParent(
      renameFolder?.parent_id != null
        ? String(renameFolder.parent_id)
        : parentId != null
          ? String(parentId)
          : "none",
    );
  }, [isOpen, renameFolder, parentId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!folderName.trim()) {
      toast({
        title: "Error",
        description: "Folder name cannot be empty",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const parent_id =
        selectedParent === "none" ? null : parseInt(selectedParent, 10);

      if (isRename && renameFolder) {
        await put(`/folders/${renameFolder.id}`, {
          name: folderName.trim(),
          parent_id:
            parent_id === renameFolder.id ? renameFolder.parent_id : parent_id,
        });
        toast({
          title: "Folder updated",
          description: `"${folderName.trim()}" saved`,
        });
      } else {
        await post("/folders", {
          name: folderName.trim(),
          parent_id,
        });
        toast({
          title: "Folder created",
          description: `"${folderName.trim()}" is ready`,
        });
      }

      setFolderName("");
      onFolderCreated();
      onClose();
    } catch (error) {
      console.error("Error saving folder:", error);
      toast({
        title: "Error",
        description: isRename
          ? "Failed to update folder"
          : "Failed to create folder",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const parentOptions = folders.filter((f) => f.id !== renameFolder?.id);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isRename ? "Rename folder" : "Create folder"}
            </DialogTitle>
            <DialogDescription>
              {isRename
                ? "Update the name or nest this folder under another."
                : "Organize documents into folders. You can nest folders if you like."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="folderName">Name</Label>
              <Input
                id="folderName"
                placeholder="Sermon series"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Parent folder</Label>
              <Select value={selectedParent} onValueChange={setSelectedParent}>
                <SelectTrigger>
                  <SelectValue placeholder="None (top level)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (top level)</SelectItem>
                  {parentOptions.map((folder) => (
                    <SelectItem key={folder.id} value={String(folder.id)}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading
                ? "Saving…"
                : isRename
                  ? "Save"
                  : "Create folder"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
